import { CanAccess } from '@modules/common/components/CanAccess';
import {
  EscHotKeyIcon,
  ReturnHotKeyIcon,
} from '@modules/common/components/HotKeyIcons';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { getAnalysisTypeDisplayName } from '@modules/dashboard/helpers';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import {
  AnalysisUseCase,
  PermissionOperation,
  PermissionResource,
} from 'generated/graphql';
import { ChangeEvent, useState } from 'react';
import { useToggle } from 'react-use';
import { Scope } from 'v2/core/entities/scope';

interface ChangeAnalysisScopeModalContentProps {
  analysisScope: Scope;
  onScopeSave: (scope: Scope) => Promise<void>;
  onClose: () => void;
  useCase: AnalysisUseCase;
}

export const ChangeAnalysisScopeModalContent = ({
  analysisScope,
  onScopeSave,
  onClose,
  useCase,
}: ChangeAnalysisScopeModalContentProps) => {
  const [isLoading, setIsLoading] = useToggle(false);
  const [scope, setScope] = useState(analysisScope);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    onScopeSave(scope);
  };

  const artefactTypeLabel = getAnalysisTypeDisplayName(useCase);

  const isPublic = scope === Scope.PUBLIC;

  return (
    <>
      <ModalHeader title="Change Discoverability" />
      <Box component="form" onSubmit={onSubmit}>
        <DialogContent dividers>
          <Stack direction="row" justifyContent="space-between">
            <Stack pr={2} spacing={1}>
              <Typography variant="subtitle2">
                {`${artefactTypeLabel} discovery`.toUpperCase()}
              </Typography>
              <Stack>
                <Typography variant="caption">
                  Discoverable {artefactTypeLabel} is visible to anyone within
                  the organization. Otherwise, {artefactTypeLabel} can only be
                  accessed by its shareable link.
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="center">
              <>
                <CanAccess
                  resource={PermissionResource.Artefact}
                  operation={PermissionOperation.Write}
                >
                  <Switch
                    checked={isPublic}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setScope(e.target.checked ? Scope.PUBLIC : Scope.PRIVATE)
                    }
                  />
                </CanAccess>
                <Typography minWidth="120px" variant="body1">
                  {isPublic ? 'Discoverable' : 'Undiscoverable'}
                </Typography>
              </>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Stack direction="row" m={2} spacing={3}>
            <Button
              variant="outlined"
              onClick={() => onClose()}
              endIcon={<EscHotKeyIcon />}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              type="submit"
              loading={isLoading}
              disabled={scope === analysisScope}
              endIcon={
                <ReturnHotKeyIcon isDisabled={scope === analysisScope} />
              }
            >
              Update
            </LoadingButton>
          </Stack>
        </DialogActions>
      </Box>
    </>
  );
};
